import { useEffect, useState } from "react";
import { useTranslate } from "@refinedev/core";
import {
  ArrowRight,
  CalendarClock,
  ChartColumn,
  PawPrint,
  Lightbulb,
  MapPinned,
  Sparkles,
  Table2,
} from "lucide-react";

import {
  exporteerExcel,
  lijstOnderdelen,
  voerAnalyseUit,
  lijstGemeenten,
  lijstVestigingen,
  haalKerncijfers,
  haalRecenteOpnames,
  type AnalyseStand,
  type Gebied,
  type Kerncijfer,
  type RecenteOpname,
  voerOnderdeelUit,
  voerVraagUit,
  InsightError,
} from "../api/insights";
import { BewaardeAnalyses } from "./BewaardeAnalyses";
import {
  DataInsightFilters, periodeSnelkeuzes, type FilterKeuze, type Onderdeel,
} from "./DataInsightFilters";
import { Samenvatting, totaalVan, type Maat, type SamenvattingGroep } from "./Samenvatting";
import { cumuleer } from "./cumulatief";
import { DonutGrafiek, type DonutDeel } from "./DonutGrafiek";
import { filterOmschrijving } from "./filterOmschrijving";
import { Kerncijfers } from "./Kerncijfers";
import { RecenteOpnames } from "./RecenteOpnames";
import { HerkomstKaart } from "./HerkomstKaart";

/**
 * Data &amp; Insight: kiezen uit vaste onderdelen, of zelf een vraag stellen.
 *
 * <p>Twee wegen naar hetzelfde antwoord, en met opzet allebei. De <b>vaste onderdelen</b> zijn wat
 * het oude statistiekenscherm had: een lijst met filters, waar je zonder na te denken de cijfers uit
 * haalt die je elk kwartaal nodig hebt. Het <b>vraagveld</b> is voor alles wat niet in die lijst
 * staat. Alleen de lijst zou betekenen dat elke nieuwe vraag een programmeur kost; alleen het
 * vraagveld zou betekenen dat je elke maand opnieuw moet formuleren wat je vorige maand ook al
 * vroeg — en dan is elk cijfer net weer anders afgebakend.
 *
 * <p>Onder water is er één weg: een onderdeel bouwt hetzelfde plan als een getypte vraag, dus beide
 * lopen langs dezelfde validator, dezelfde vestigingsafscherming en dezelfde uitvoerder.
 */

/** Vragen om mee te beginnen, voor wie zelf iets wil vragen. */
const VOORBEELDEN: { tekst: string; icoon: React.ReactNode }[] = [
  { tekst: "Hoeveel dieren zitten er nu in de opvang?", icoon: <PawPrint /> },
  { tekst: "Hoeveel dieren kwamen er per maand binnen?", icoon: <ChartColumn /> },
  { tekst: "Wat is de gemiddelde verblijfsduur?", icoon: <CalendarClock /> },
  { tekst: "Hoeveel aanwezige dieren hebben geen chipnummer?", icoon: <Table2 /> },
];

export interface AntwoordDto {
  mode: string;
  explanation: string | null;
  question: string | null;
  unsupportedParts: string[];
  total: number;
  error: string | null;
  items: { id: string; title: string; subtitle: string | null;
           details: { label: string; value: string }[] }[];
  groepen?: SamenvattingGroep[] | null;
  groeperingen?: string[] | null;
  maat?: string | null;
}

const NU = new Date().getFullYear();

/**
 * De stand waarmee het scherm opent: de laatste twaalf maanden, per maand.
 *
 * <p>Eerst was dit "vorig jaar t/m dit jaar" per jaar. Dat gaf twee staven waarvan de rechter een
 * half jaar was, en het maakte de vergelijking op de kaarten onleesbaar: "dezelfde periode vorig
 * jaar" schuift het bereik één jaar op, dus bij een bereik van twee jaar overlapt het met zichzelf
 * en telt een heel jaar in beide mee.
 *
 * <p>Twaalf maanden per maand doet alle drie de dingen goed: je ziet een verloop in plaats van twee
 * blokken, de periode is volledig verstreken, en de vorige periode sluit er precies op aan zonder
 * overlap.
 *
 * <p>Als constante en niet inline in {@code useState}, zodat het openen dezelfde stand kan
 * gebruiken zonder hem uit de huidige stand te hoeven vissen.
 */
const BEGINKEUZE: FilterKeuze = {
  onderdeel: "",
  ...(periodeSnelkeuzes().find((s) => s.sleutel === "laatste12")?.stand
    ?? { vanJaar: NU - 1, totJaar: NU, vanDatum: null, totDatum: null, eenheid: "JAAR" as const }),
  diersoorten: [], uitsplitsing: "", gemeente: "",
  ouderDan: null, ouderDanEenheid: "MAANDEN", jongerDan: null, jongerDanEenheid: "MAANDEN",
  cumulatief: false, vestigingen: [],
};

/**
 * De onderdelen die als snelkoppeling in de zijkolom staan.
 *
 * <p>Dezelfde onderdelen als in de lijst, alleen sneller bereikbaar. Vandaar alleen hun id: een
 * eigen naam of uitleg hier zou naast de catalogus gaan lopen zodra iemand daar iets bijstelt.
 */
const SNEL = ["intake-aantal", "bestemming-aantal", "verblijf-gemiddelde",
  "bezetting-aanwezig", "intake-zwerf"];

/**
 * De verdeling over de laatste as, voor de ring naast de grafiek.
 *
 * <p>Bij twee assen is de laatste de categorie: per diersoort, per vestiging. De periodes eronder
 * worden opgeteld, want een ring gaat over de verdeling en niet over het verloop.
 *
 * <p>Alleen bij een telling. Gemiddelden en percentages optellen levert een ring op waarvan de
 * segmenten kloppen en het geheel niet bestaat — en juist een ring belooft dat de delen samen het
 * geheel zijn.
 */
export function verdelingVan(groepen: SamenvattingGroep[], maat: Maat = "AANTAL"): DonutDeel[] {
  if (maat !== "AANTAL") return [];
  const uit: DonutDeel[] = [];
  for (const g of groepen) {
    if (g.labels.length < 2 || g.waarde == null) continue;
    const label = g.labels[g.labels.length - 1];
    const bestaand = uit.find((d) => d.label === label);
    if (bestaand) bestaand.waarde += g.waarde;
    else uit.push({ label, waarde: g.waarde });
  }
  return uit;
}

export function DataInsight({ onOpenDier }: {
  /** Doorklikken naar een dier vanuit een lijstantwoord. */
  onOpenDier?: (id: string) => void;
}) {
  const t = useTranslate();

  const [onderdelen, setOnderdelen] = useState<Onderdeel[] | null>(null);
  const [lijstFout, setLijstFout] = useState<string | null>(null);
  const [gebieden, setGebieden] = useState<Gebied[]>([]);
  const [vestigingen, setVestigingen] = useState<string[]>([]);
  const [kerncijfers, setKerncijfers] = useState<Kerncijfer[]>([]);
  // Gaat omhoog zodra het scherm de filterstand met opzet zet, zoals bij het openen van een
  // bewaarde analyse. De filterbalk gebruikt dat om een bewuste stand te onderscheiden van een
  // gewone hertekening.
  const [overnemen, setOvernemen] = useState(0);
  const [opnames, setOpnames] = useState<RecenteOpname[]>([]);
  const [keuze, setKeuze] = useState<FilterKeuze>(BEGINKEUZE);
  const [antwoord, setAntwoord] = useState<AntwoordDto | null>(null);
  const [uitleg, setUitleg] = useState<string | null>(null);
  const [bezig, setBezig] = useState(false);
  /**
   * De tekst bij een mislukte aanroep.
   *
   * <p>Een 404 betekent hier niet "niet gevonden" maar "deze installatie heeft Data & Insight niet
   * aanstaan": de controllers zitten achter een @ConditionalOnProperty op
   * {@code fawkes.api.search.ai.enabled}, dus zonder die vlag bestáán de endpoints niet. Op het scherm
   * stond dan kaal "HTTP 404", en daar gaat iemand een kapotte pagina van zoeken terwijl er niets
   * kapot is -- er staat alleen iets uit. Gezien bij Crailo, 15-10-2026.
   */
  const foutTekst = (e: unknown): string => {
    if (e instanceof InsightError && e.status === 404) return t("dataInsight.nietIngeschakeld");
    return e instanceof Error && e.message ? e.message : t("dataInsight.nietBeschikbaar");
  };

  const [fout, setFout] = useState<string | null>(null);

  // Welk tabblad open staat. Lokaal: het is een manier van vragen, geen andere vraag, en het
  // antwoord eronder blijft staan als je omschakelt.
  // De herkomstkaart is een derde tabblad en geen weergave van een analyse: hij telt per plek in plaats
  // van langs een as, en heeft daarom zijn eigen, kortere filters.
  const [tab, setTab] = useState<"AI" | "STANDAARD" | "KAART">("STANDAARD");

  // Het vraagveld staat los van de filterbalk: de een is kiezen, de ander typen.
  const [tekst, setTekst] = useState("");
  const [vraag, setVraag] = useState("");
  const [uitgeklapt, setUitgeklapt] = useState<string[] | null>(null);

  useEffect(() => {
    lijstOnderdelen()
      .then((l) => {
        const lijst = l as Onderdeel[];
        setOnderdelen(lijst);
        // Het eerste onderdeel meteen uitrekenen. Wachten tot iemand op Filter drukt leek netjes
        // — geen zware analyse starten die niemand vroeg — maar het levert een scherm op waarvan
        // de onderste helft leeg is, en dat leest als kapot in plaats van als afwachtend.
        //
        // Vanuit de beginstand en niet vanuit een setKeuze-updater. Dat stond er eerst wél, met
        // draaiOnderdeel er middenin: een updater hoort puur te zijn, en React roept hem in
        // StrictMode dubbel aan om precies dat aan te wijzen. Gevolg was vier analyses bij één
        // keer openen — en elke analyse schrijft een regel in het auditlogboek. draaiOnderdeel
        // zet de stand zelf, dus die updater was ook niet nodig.
        if (lijst.length > 0) {
          draaiOnderdeel({ ...BEGINKEUZE, onderdeel: lijst[0].id });
        }
      })
      .catch((e: unknown) => {
        // Niet stilzwijgend leeg laten: dan verdwijnt de hele filterbalk en ziet niemand waarom.
        setOnderdelen([]);
        setLijstFout(foutTekst(e));
      });
    // De gemeentelijst van het inboeken hergebruiken. Een tweede lijst met dezelfde gemeenten zou
    // er ooit eentje missen, en dan levert hetzelfde filter op twee schermen een ander antwoord.
    // Mislukt het, dan blijft alleen "alle gemeenten" over: één filter minder is niet erg, een
    // scherm dat helemaal niet opent wel.
    // De woonplaatsen komen mee: de gemeenten zijn daarmee uitgebreid, en op een woonplaats
    // filteren is preciezer dan op de gemeente eromheen.
    lijstGemeenten()
      .then((g) => setGebieden(g))
      .catch(() => setGebieden([]));
    // De vestigingen waar deze gebruiker bij mag. Mislukt het, dan verdwijnt alleen de keuze en
    // blijft het scherm op de huidige vestiging staan — dat is de veilige kant.
    lijstVestigingen()
      .then((v) => setVestigingen(v))
      .catch(() => setVestigingen([]));
  }, []);

  const toon = (r: unknown, verklaring: string | null) => {
    const a = r as AntwoordDto;
    setAntwoord(a);
    setUitleg(verklaring ?? a.explanation);
    setUitgeklapt(null);
  };

  const draaiOnderdeel = (k: FilterKeuze) => {
    setKeuze(k);
    setFout(null);
    setBezig(true);
    setVraag("");
    // De kaarten volgen dezelfde filters als de analyse. Mislukt dat, dan verdwijnen alleen de
    // kaarten: een analyse zonder kaarten is nog steeds een antwoord, en een foutmelding over de
    // kaarten zou de aandacht wegtrekken van het cijfer waar iemand voor kwam.
    haalKerncijfers(k).then(setKerncijfers).catch(() => setKerncijfers([]));
    // De laatste opnames volgen alleen de vestigingkeuze: dit is een blik op wat er net binnenkwam
    // en geen uitsnede van de gekozen periode. Zou de periode meedoen, dan staat de tabel leeg bij
    // een analyse over 2019 — en dan lijkt hij kapot terwijl hij precies doet wat er staat.
    haalRecenteOpnames(k.vestigingen).then(setOpnames).catch(() => setOpnames([]));
    voerOnderdeelUit(k)
      .then((r) => toon(r, (onderdelen ?? []).find((o) => o.id === k.onderdeel)?.uitleg ?? null))
      .catch((e: unknown) => setFout(foutTekst(e)))
      .finally(() => setBezig(false));
  };

  const stelVraag = (v: string, groep?: string[] | null) => {
    const schoon = v.trim();
    if (!schoon) return;
    setFout(null);
    setBezig(true);
    setTekst(schoon);
    setVraag(schoon);
    voerVraagUit(schoon, groep ?? undefined)
      .then((r) => {
        const a = r as AntwoordDto;
        setAntwoord(a);
        setUitleg(a.explanation);
      })
      .catch((e: unknown) => setFout(foutTekst(e)))
      .finally(() => setBezig(false));
  };

  const openBewaarde = (id: string, stand: AnalyseStand | null) => {
    setFout(null);
    setBezig(true);
    // De filterbalk mee: zonder dit draait het bewaarde plan wel, maar staat de balk nog op de
    // vorige keuze — en dan staat er een cijfer met filters ernaast die er niet bij horen.
    if (stand?.onderdeel) {
      setKeuze((k) => ({
        ...k,
        onderdeel: stand.onderdeel,
        vanJaar: stand.vanJaar ?? k.vanJaar,
        totJaar: stand.totJaar ?? k.totJaar,
        vanDatum: stand.vanDatum ?? null,
        totDatum: stand.totDatum ?? null,
        diersoorten: stand.diersoorten ?? [],
        uitsplitsing: stand.uitsplitsing ?? "",
        gemeente: stand.gemeente ?? "",
        ouderDan: stand.ouderDan ?? null,
        ouderDanEenheid:
          (stand.ouderDanEenheid as FilterKeuze["ouderDanEenheid"]) ?? "MAANDEN",
        jongerDan: stand.jongerDan ?? null,
        jongerDanEenheid:
          (stand.jongerDanEenheid as FilterKeuze["jongerDanEenheid"]) ?? "MAANDEN",
        eenheid: (stand.eenheid as FilterKeuze["eenheid"]) ?? k.eenheid,
        vestigingen: stand.vestigingen ?? [],
      }));
      setOvernemen((n) => n + 1);
      setTab("STANDAARD");
    }
    voerAnalyseUit(id)
      .then((r) => toon(r, null))
      .catch((e: unknown) => setFout(foutTekst(e)))
      .finally(() => setBezig(false));
  };

  const maat: Maat = antwoord?.maat === "GEMIDDELDE" ? "GEMIDDELDE" : "AANTAL";
  const ruwe = antwoord?.groepen ?? [];
  // Cumulatief is een weergavekeuze en geen andere vraag: de cijfers van de server blijven
  // hetzelfde, ze worden alleen anders opgeteld. Daarom hier en niet in het verzoek — het omzetten
  // van de schakelaar hoeft dan niets opnieuw uit te rekenen.
  const groepen = keuze.cumulatief && !vraag ? cumuleer(ruwe, maat) : ruwe;
  const isSamenvatting = groepen.length > 0;
  const titel = (onderdelen ?? []).find((o) => o.id === keuze.onderdeel)?.naam;

  // De verdeling per diersoort voor de ring naast de grafiek. Uit dezelfde uitkomst en niet uit een
  // tweede vraag: twee queries over hetzelfde zouden op twee momenten kunnen draaien, en dan staan
  // er twee cijfers naast elkaar die niet bij elkaar horen.
  const ringdelen = verdelingVan(ruwe);

  return (
    <div className="fawkes-di">
      <div className="fawkes-di__body">
        <main className="fawkes-di__hoofd">
          {/* Twee ingangen naar hetzelfde antwoord, en de tabs zeggen welke je gebruikt. Ze staan
              niet naast elkaar op één scherm omdat ze om iets anders vragen: de een om een vraag in
              je eigen woorden, de ander om een keuze uit een lijst. Alles eronder — de filters, de
              kaarten, de grafiek — is voor beide hetzelfde. */}
          <section className="fawkes-di__paneel">
            <div className="fawkes-di__tabs" role="tablist" aria-label={t("dataInsight.title")}>
              <button
                type="button" role="tab"
                aria-selected={tab === "AI"}
                className={"fawkes-di__tab" + (tab === "AI" ? " fawkes-di__tab--aan" : "")}
                onClick={() => setTab("AI")}
              >
                <Sparkles aria-hidden="true" />
                {t("dataInsight.tabAi")}
              </button>
              <button
                type="button" role="tab"
                aria-selected={tab === "STANDAARD"}
                className={"fawkes-di__tab"
                  + (tab === "STANDAARD" ? " fawkes-di__tab--aan" : "")}
                onClick={() => setTab("STANDAARD")}
              >
                <ChartColumn aria-hidden="true" />
                {t("dataInsight.tabStandaard")}
              </button>
              <button
                type="button" role="tab"
                aria-selected={tab === "KAART"}
                className={"fawkes-di__tab" + (tab === "KAART" ? " fawkes-di__tab--aan" : "")}
                onClick={() => setTab("KAART")}
              >
                <MapPinned aria-hidden="true" />
                {t("dataInsight.tabKaart")}
              </button>
            </div>

            {tab === "AI" ? (
              <div className="fawkes-di__vraagblok">
                <div className="fawkes-di__hero-kop">
                  <span className="fawkes-di__hero-icoon" aria-hidden="true"><Sparkles /></span>
                  <div>
                    <h2 className="fawkes-di__hero-titel">{t("dataInsight.heroTitel")}</h2>
                    <p className="fawkes-di__hero-sub">{t("dataInsight.heroSub")}</p>
                  </div>
                </div>
                <form className="fawkes-di__vraagbalk"
                  onSubmit={(e) => { e.preventDefault(); stelVraag(tekst); }}>
                  <span className="fawkes-di__vraagicoon" aria-hidden="true"><Sparkles /></span>
                  <input
                    className="fawkes-di__vraagveld"
                    value={tekst}
                    onChange={(e) => setTekst(e.target.value)}
                    placeholder={t("dataInsight.placeholder")}
                    aria-label={t("dataInsight.vraagLabel")}
                  />
                  <button type="submit" className="fawkes-di__vraagknop"
                    disabled={!tekst.trim() || bezig}>
                    <Sparkles aria-hidden="true" />
                    {t("dataInsight.stelVraag")}
                    <ArrowRight aria-hidden="true" />
                  </button>
                </form>
                <div className="fawkes-di__voorbeelden">
                  <span className="fawkes-di__voorbeelden-kop">{t("dataInsight.voorbeelden")}</span>
                  {VOORBEELDEN.map((v) => (
                    <button key={v.tekst} type="button" className="fawkes-di__voorbeeld"
                      onClick={() => stelVraag(v.tekst)}>
                      <span className="fawkes-di__voorbeeld-icoon" aria-hidden="true">
                        {v.icoon}
                      </span>
                      {v.tekst}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {/* Zolang de lijst nog komt: niets tonen. Is hij er en leeg, dan is er iets mis en
                hoort dat er te staan — een verdwenen filterbalk is geen boodschap. */}
            {tab === "KAART" ? <HerkomstKaart vestigingen={vestigingen} t={t} /> : null}

            {tab !== "KAART" && onderdelen != null && onderdelen.length === 0 ? (
              <p className="fawkes-di__fout" role="alert">
                {lijstFout ?? t("dataInsight.geenOnderdelen")}
              </p>
            ) : null}

            {tab !== "KAART" && onderdelen != null && onderdelen.length > 0 ? (
              <DataInsightFilters
                onderdelen={onderdelen}
                gebieden={gebieden}
                vestigingen={vestigingen}
                keuze={keuze}
                overnemen={overnemen}
                onToepassen={draaiOnderdeel}
                bezig={bezig}
                // Op het AI-tabblad is de onderdeelkeuze niet aan de orde: daar bepaalt de vraag
                // wat er geteld wordt, en een keuzelijst ernaast zou suggereren dat die meedoet.
                metOnderdeel={tab === "STANDAARD"}
                t={t}
              />
            ) : null}
          </section>

          {/* Op de kaart horen de analyse, de kaarten en de laatste opnames er niet onder: die gaan over
              de gekozen analyse, en de kaart heeft zijn eigen filters. */}
          {tab !== "KAART" ? (<>
          {fout ? <p className="fawkes-di__fout" role="alert">{fout}</p> : null}
          {bezig ? <p className="fawkes-muted">{t("common.loading")}</p> : null}
          {antwoord?.error ? (
            <p className="fawkes-di__fout" role="alert">{antwoord.error}</p>
          ) : null}

          {/* De vraag kon twee kanten op, of we begrepen de afbakening niet. Dan een wedervraag en
              geen getal: een grafiek met een verkeerd getal erin zet iemand in een verslag. */}
          {antwoord && antwoord.mode === "clarification" ? (
            <div className="fawkes-di__wedervraag">
              <p className="fawkes-di__wedervraag-tekst">
                {antwoord.question ?? t("dataInsight.wedervraag")}
              </p>
              {antwoord.unsupportedParts.length > 0 ? (
                <p className="fawkes-di__nietBegrepen">
                  {t("dataInsight.nietBegrepen")}: {antwoord.unsupportedParts.join(" · ")}
                </p>
              ) : null}
            </div>
          ) : null}

          {/* De vier kaarten. Ze volgen dezelfde filters als de analyse, maar wélke vier het zijn
              staat vast: dat zijn de maten waar een opvang op stuurt. */}
          <Kerncijfers cijfers={kerncijfers} t={t} />

          {isSamenvatting ? (
            <div className={"fawkes-di__grafieken"
              + (ringdelen.length > 1 ? " fawkes-di__grafieken--metRing" : "")}>
              <section className="fawkes-di__uitkomst">
                <Samenvatting
                  groepen={groepen}
                  groeperingen={antwoord?.groeperingen ?? []}
                  titel={!vraag ? titel : undefined}
                  alsKaarten
                  maat={maat}
                  totaal={keuze.cumulatief && !vraag ? totaalVan(ruwe, maat) : undefined}
                  // Cumulatief staat bij de grafiek en niet in de filterbalk: het is een manier om
                  // dezelfde cijfers te tonen en geen andere vraag. Bij een gemiddelde valt er
                  // niets op te tellen — de som van 43,5 en 21,0 zegt niets over de dieren
                  // eronder — dus dan is er geen schakelaar in plaats van een schakelaar die
                  // niets doet.
                  cumulatief={!vraag && maat === "AANTAL"
                    ? { aan: keuze.cumulatief,
                        onZet: (aan) => setKeuze((k) => ({ ...k, cumulatief: aan })) }
                    : undefined}
                  onUitklappen={(labels) => {
                    setUitgeklapt(labels);
                    if (vraag) stelVraag(vraag, labels);
                  }}
                  export={{
                    vraag: vraag || (titel ?? ""),
                    // De toegepaste filters mee in de kop van het bestand. Zonder dit is een los
                    // csv-bestand drie maanden later niet meer te plaatsen: "1.248 binnengekomen"
                    // is een ander getal met en zonder leeftijdsgrens, en dan wordt er gerekend
                    // met cijfers waarvan niemand meer weet waar ze over gingen. Bij een getypte
                    // vraag niet: daar staat de afbakening in de vraag zelf, en de filterbalk zegt
                    // dan niets over wat er gerekend is.
                    filters: vraag ? [] : filterOmschrijving(keuze, t),
                    // De weergave en cumulatief gaan mee: het bestand tekent wat er op het scherm stond.
                    onExcel: (weergave) => exporteerExcel({
                      vraag: vraag || (titel ?? ""), labels: uitgeklapt ?? undefined,
                      grafiek: weergave, cumulatief: !vraag && keuze.cumulatief }),
                  }}
                  t={t}
                />
              </section>

              {/* De ring alleen bij een uitsplitsing: zonder tweede as is er één categorie, en dan
                  is een ring van honderd procent een cirkel zonder mededeling. */}
              {ringdelen.length > 1 ? (
                <section className="fawkes-samenvatting fawkes-di__ringkaart">
                  <div className="fawkes-samenvatting__kop">
                    <span className="fawkes-samenvatting__kopicoon" aria-hidden="true">
                      <ChartColumn />
                    </span>
                    <div className="fawkes-samenvatting__koptekst">
                      <h3 className="fawkes-samenvatting__titel">
                        {t("dataInsight.verdelingKop")}
                      </h3>
                      <p className="fawkes-samenvatting__assen">
                        {(antwoord?.groeperingen ?? []).slice(-1)[0] ?? ""}
                      </p>
                    </div>
                  </div>
                  <DonutGrafiek
                    delen={ringdelen}
                    eenheid={t("dataInsight.eenheidDieren")}
                    toon={(w) => new Intl.NumberFormat("nl-NL",
                      { maximumFractionDigits: 0 }).format(w)}
                  />
                </section>
              ) : null}
            </div>
          ) : null}

          {isSamenvatting && uitleg ? (
            <p className="fawkes-di__definitie">{uitleg}</p>
          ) : null}

          {antwoord && !isSamenvatting && antwoord.items.length > 0 ? (
            <div className="fawkes-di__lijst">
              <div className="fawkes-di__lijst-kop">
                <span>{antwoord.total} {t("dataInsight.resultaten")}</span>
                <button
                  type="button"
                  className="fui-button fui-button--secondary"
                  onClick={() => {
                    setFout(null);
                    void exporteerExcel({ vraag: vraag || (titel ?? "") })
                      .catch((e: unknown) => setFout(e instanceof Error ? e.message
                        : t("zoekresultaten.samenvatting.exportFout")));
                  }}
                >
                  {t("zoekresultaten.samenvatting.exportExcel")}
                </button>
              </div>
              <ul className="fawkes-di__regels">
                {antwoord.items.map((item) => (
                  <li key={item.id} className="fawkes-di__regel">
                    <button type="button" className="fawkes-di__regel-titel"
                      onClick={() => onOpenDier?.(item.id)}>
                      {item.title}
                    </button>
                    {item.subtitle ? (
                      <span className="fawkes-di__regel-sub">{item.subtitle}</span>
                    ) : null}
                    {item.details.map((d) => (
                      <span key={d.label} className="fawkes-di__regel-detail">
                        {d.label}: {d.value}
                      </span>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {/* De laatste opnames: de enige plek op dit scherm met losse records in plaats van
              cijfers. Een grafiek zegt hoeveel, deze tabel zegt wie — en zonder dat laatste is een
              getal niet na te lopen. */}
          <RecenteOpnames opnames={opnames} t={t} />
          </>) : null}
        </main>

        <aside className="fawkes-di__zij">
          <BewaardeAnalyses
            huidigeVraag={vraag}
            // De stand alleen bij een standaardanalyse: een getypte vraag heeft geen onderdeel.
            huidigeStand={vraag ? undefined : {
              onderdeel: keuze.onderdeel,
              vanJaar: keuze.vanJaar,
              totJaar: keuze.totJaar,
              vanDatum: keuze.vanDatum,
              totDatum: keuze.totDatum,
              diersoorten: keuze.diersoorten,
              uitsplitsing: keuze.uitsplitsing,
              eenheid: keuze.eenheid,
              gemeente: keuze.gemeente,
              ouderDan: keuze.ouderDan,
              ouderDanEenheid: keuze.ouderDanEenheid,
              jongerDan: keuze.jongerDan,
              jongerDanEenheid: keuze.jongerDanEenheid,
              vestigingen: keuze.vestigingen,
            }}
            // Bewaren mag ook zonder getypte vraag: een standaardanalyse mét zijn filters is
            // precies zo goed te bewaren, en dat was tot nu toe niet mogelijk.
            kanBewaren={Boolean(antwoord) && !antwoord?.error
              && antwoord?.mode !== "clarification"
              && (Boolean(vraag) || Boolean(keuze.onderdeel))}
            onOpenen={openBewaarde}
            // De veelgebruikte analyses: een handvol vaste onderdelen om snel te openen. Ze staan
            // in de zijkolom en niet in de onderdeellijst omdat het dezelfde onderdelen zijn — dit
            // is een snelkoppeling en geen tweede catalogus.
            snelkoppelingen={(onderdelen ?? []).filter((o) => SNEL.includes(o.id))
              .map((o) => ({ id: o.id, naam: o.naam, uitleg: o.familie }))}
            onSnel={(id) => draaiOnderdeel({ ...keuze, onderdeel: id })}
            t={t}
          />

          {/* Een voorbeeldvraag om verder te komen. Staat in de zijkolom en niet in de hero: daar
              staan er al vier, en dit is er een die je pakt als die vier je niet verder hielpen. */}
          <section className="fawkes-di__tip">
            <span className="fawkes-di__tip-icoon" aria-hidden="true"><Lightbulb /></span>
            <div>
              <h3 className="fawkes-di__tip-kop">{t("dataInsight.tipKop")}</h3>
              <p className="fawkes-di__tip-tekst">{t("dataInsight.tipTekst")}</p>
              <button type="button" className="fawkes-di__tip-knop"
                onClick={() => stelVraag(t("dataInsight.tipVraag"))}>
                {t("dataInsight.tipVraag")}
                <ArrowRight aria-hidden="true" />
              </button>
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}
